import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { uuid } from 'uuidv4';
import { BulkUploadDialog } from '@dentira/dentira-bulk-upload-widget';

import { CopyIcon } from './Styles/Icons';
import { FlexRow, Bold, InputGroup, Select, MenuItem, SectionRow, InputAdornment, Search, Pagination, ManufacturerMenuItem } from './Styles/AddBulkItems';

import { updateErrorMessage, updateSuccessMessage, updateMessage, updateError } from '../../actions/commonActions';
import { fetchAutocompleteSubCategory, fetchAutocompleteSubSubCategory, fetchAutocompleteSubCategoryV2, fetchAutocompleteSubSubCategoryV2 } from '../../actions/inventoryActionsV2';

import { getRestrictedFields, STOCK_STATUS, isSubSubCategoryRequired, hideDSOInfo, getSellerPortalConfigFlag } from '../../commons/utils';
import { API_KEY, HOST } from '../../commons/backend/api-config';

// Talks to sellerportal's v2/bulk-upload worker (BULK_INVENTORY_CREATE/BULK_INVENTORY_UPDATE)
// through @dentira/dentira-bulk-upload-widget and the external bulkupload-api.dentira.com
// gateway, instead of AddBulkItems.jsx's sellerportal /bulk /bulk-update routes + SPJ polling.
// Deliberately a separate component, not a branch inside AddBulkItems.jsx - see InventoryV2/
// index.jsx for the SWITCH_V2 that picks between them.
class AddBulkItemsV2 extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			filteredManufacturerOptions: [],
			filteredManufacturerCount: 0,
			manufacturerSearchText: '',
			manufacturerPageNumber: 0,
			manufacturerPageSize: 25,
			manufacturer: '',
			mappedSupplier: '',
			categoryOptions: [],
			category: '',
			subCategory: '',
			subSubCategory: '',
			packaging: '',
			mappedDSOIds: '',
			uom: '',
		};
	}

	async componentDidMount() {
		this.setDefaultState();
	}

	async componentDidUpdate(prevProps, prevState) {
		if (prevState.manufacturerSearchText !== this.state.manufacturerSearchText) {
			let { manufacturerOptions } = this.props;
			let { manufacturerSearchText, manufacturerPageSize } = this.state;

			let manufacturerList = [];
			manufacturerList = manufacturerOptions.filter((option) => option.name.toUpperCase().includes(manufacturerSearchText.toUpperCase()));
			this.setState({
				manufacturerPageNumber: 0,
				filteredManufacturerCount: manufacturerList.length,
				filteredManufacturerOptions: manufacturerList.slice(0, manufacturerPageSize),
			});
		}

		if (prevState.manufacturerPageNumber !== this.state.manufacturerPageNumber) {
			let { manufacturerOptions } = this.props;
			let { manufacturerPageNumber, manufacturerPageSize } = this.state;

			this.setState({
				filteredManufacturerOptions: manufacturerOptions.slice(manufacturerPageNumber * manufacturerPageSize, (manufacturerPageNumber + 1) * manufacturerPageSize),
			});
		}

		if (prevState.manufacturerPageSize !== this.state.manufacturerPageSize) {
			let { manufacturerOptions } = this.props;
			let { manufacturerPageSize } = this.state;

			this.setState({
				manufacturerPageNumber: 0,
				filteredManufacturerOptions: manufacturerOptions.slice(0, manufacturerPageSize),
			});
		}

		if (this.state.category && prevState.category !== this.state.category) {
			let { category } = this.state;
			if (this.props.switchToInventoryV2) {
				await this.props.fetchAutocompleteSubCategoryV2(category);
			} else {
				await this.props.fetchAutocompleteSubCategory(category);
			}
			this.setState({ subCategory: '' });
		}

		if (isSubSubCategoryRequired(this.props.groupId) && this.state.subCategory && prevState.subCategory !== this.state.subCategory) {
			let { category, subCategory } = this.state;
			if (this.props.switchToInventoryV2) {
				await this.props.fetchAutocompleteSubSubCategoryV2(category, subCategory);
			} else {
				await this.props.fetchAutocompleteSubSubCategory(category, subCategory);
			}
			this.setState({ subSubCategory: '' });
		}
	}

	setDefaultState = async (next = null) => {
		let { manufacturerOptions } = this.props;

		this.setState(
			{
				manufacturerPageNumber: 0,
				filteredManufacturerOptions: (manufacturerOptions || []).slice(0, this.state.manufacturerPageSize),
				filteredManufacturerCount: (manufacturerOptions || []).length,
				manufacturer: '',
				mappedSupplier: '',
				packaging: '',
				category: '',
				subCategory: '',
				subSubCategory: '',
				mappedDSOIds: '',
				uom: '',
			},
			next
		);
	};

	handleInput = (key, value, next = null) => {
		this.handleCopyInput(value);
		this.setState(
			{ [key]: value },
			next
		);
	};

	handleCopyInput = (value) => {
		navigator.clipboard.writeText(value);
		this.props.updateMessage(true, 'Copied to clipboard');
	};

	// Builds the config for @dentira/dentira-bulk-upload-widget's <BulkUploadDialog>. `type` must
	// match the use-case-type strings v2/bulk-upload/inventory/*.js's getBulkUploadUseCaseType()
	// returns. downloadRequestPayload/uploadRequestPayload become the job's requestPayload, read by
	// BaseInventoryBulkUpload.init() on the worker side.
	//
	// Safe to recompute on every render - the widget's own internal effects key off primitives
	// (isOpen, checkingStatus, etc.), not config's object identity, and the rules/pickers render as
	// `children` on the same dialog rather than a separate screen, so there's no state machine to
	// manage here at all.
	getBulkUploadWidgetConfig = () => {
		const { open, updateItems, supplier, session, isSupplierWithLivePrice, allowMultipleUOM } = this.props;
		const isDsoAdmin = (session?.user_type || []).includes('dso_seller_admin');

		return {
			type: updateItems ? 'BULK_INVENTORY_UPDATE' : 'BULK_INVENTORY_CREATE',
			isOpen: open,
			handleClose: this.props.toggle,
			title: updateItems ? 'Update Items And Status In Bulk' : 'Add Items In Bulk',
			fileType: 'EXCEL',
			session: session?.session_token || session,
			apiKey: API_KEY,
			region: HOST.toLowerCase(),
			downloadTemplateConfig: {
				hasDynamicTemplate: true,
				showDownloadButton: true,
				downloadRequestPayload: {
					supplier,
					isSupplierWithLivePrice,
					// Sent again, unchanged, on uploadRequestPayload below - the backend freezes this
					// value from whichever job carries it rather than re-checking the supplier's live
					// setting, so the template's column layout (opts/"Variants" vs uom/pkg/qty) and the
					// fields required at upload validation always agree even if this setting is changed
					// in between (see BaseInventoryBulkUpload.init()).
					allowMultipleUOM,
					// Template-only (BaseInventoryBulkUpload.resolveRestrictedFields) - which columns
					// this user/supplier is allowed to edit is a permission check, not something
					// validated again at upload time, so it doesn't need the same freeze-and-resend
					// treatment as allowMultipleUOM above.
					userType: session?.user_type,
					request_id: uuid(),
				},
			},
			uploadConfig: {
				showUploadButton: true,
				uploadRequestPayload: {
					supplier,
					isSupplierWithLivePrice,
					allowMultipleUOM,
					is_dso_admin: isDsoAdmin,
					updated_by: session?.email || '',
					request_id: uuid(),
				},
			},
			onUploadSuccess: () => {
				this.props.onSuccess();
				this.props.toggle();
			},
		};
	};

	renderInstructionsAndPickers() {
		let {
			manufacturer,
			mappedSupplier,
			packaging,
			category,
			subCategory,
			subSubCategory,
			mappedDSOIds,
			filteredManufacturerCount,
			filteredManufacturerOptions,
			manufacturerSearchText,
			manufacturerPageNumber,
			manufacturerPageSize,
			uom,
		} = this.state;
		let {
			updateItems,
			groupId,
			mappedSupplierOptions,
			packagingOptions,
			mappedDSOOptions,
			categoryOptions,
			subCategoryOptions,
			subSubCategoryOptions,
			restrictedFields,
			allowZeroPrice,
			allowMultipleUOM,
			switchToInventoryV2,
			uomOptions,
		} = this.props;

		return (
			<>
				<FlexRow style={{ margin: '0.5rem auto', textAlign: 'center' }}>Download the sample file and add item details and upload again to add multiple items to inventory at once.</FlexRow>

				<FlexRow style={{ justifyContent: 'center', textAlign: 'center', paddingTop: '0.5rem' }}>
					<Bold>Rules:</Bold>
				</FlexRow>

				<FlexRow style={{ alignSelf: 'center', paddingBottom: '0.5rem' }}>
					<ul style={{ listStyle: 'circle' }}>
						<li>
							<Bold>Do not alter</Bold> the file headers.
						</li>

						{updateItems ? (
							<>
								<li>
									Details for <Bold>locked items</Bold> cannot be updated.
								</li>

								<li>
									<Bold>Item ID</Bold> is a mandatory field.
								</li>
							</>
						) : null}

						<li>
							<Bold>Item ID, Item Name, Manufacturer, Manufacturer SKU, Item Description, Status, Price Per Item, Category, UOM</Bold> are mandatory fields.
						</li>

						<li>
							<Bold>Item Name</Bold> should be at max 150 characters in length.
						</li>

						<li>
							<Bold>Item ID</Bold> should be unique. It cannot contain underscore(_) and colon(:).
						</li>

						<li>
							<Bold>Manufacturer</Bold> should be at max 50 characters in length. It can be selected and copied from the dropdown given below.
						</li>

						<li>
							<Bold>Manufacturer SKU</Bold> should be at max 25 characters in length.
						</li>

						<li>
							<Bold>Status</Bold> should have the following values only : [
							<Bold>
								{Object.keys(STOCK_STATUS)
									.map((code) => code)
									.join(', ')}
							</Bold>
							].
						</li>

						<li>
							Multiple URLs should be separated using a <Bold>||</Bold> symbol.
						</li>

						{allowMultipleUOM
							? <li>
								<Bold>Variants</Bold> have to follow a specific format. UOM, Packaging and Quantity should be written as <Bold>UOM : Packaging : Quantity</Bold>.
								Both uom and packaging should be valid values and can be picked up from the dropdowns given below.
								Quantity should be a positive numeric value. Multiple variants have to be separated using a <Bold>||</Bold> symbol.
								For example: "CARTON : CAN : 10 || BOX : BOX : 10"
							</li>
							: <>
								<li>
									<Bold>UOM</Bold> should be only among the values given in the dropdown in the template.
								</li>

								<li>
									<Bold>Packaging</Bold> should be only among the values given in the dropdown in the template.
								</li>

								<li>
									<Bold>Quantity</Bold> should be a positive numeric value. If <Bold>Packaging</Bold> is provided then <Bold>Quantity</Bold> becomes mandatory and vice versa.
								</li>
							</>
						}

						<li>
							<Bold>Minimum Quantity, Maximum Quantity, Incremental Quantity</Bold> should be a positive numeric value.
						</li>

						<li>
							<Bold>Is Special Market?</Bold> should be following values only : [<Bold>{['TRUE', 'FALSE'].map((item) => item).join(', ')}</Bold>
							].
						</li>

						{!(hideDSOInfo(groupId) || (restrictedFields.includes('group_ids') && restrictedFields.includes('group_private'))) ? (
							<>
								<li>
									<Bold>Map To Particular DSOs</Bold> can be selected and copied from the dropdown given below. Multiple DSOs have to be separated using a <Bold>||</Bold> symbol. For
									example: "DSO-1 || DSO-2"
								</li>

								<li>
									<Bold>Make item available only for selected DSOs?</Bold> should be following values only : [<Bold>{['TRUE', 'FALSE'].map((item) => item).join(', ')}</Bold>
									].
								</li>
							</>
						) : null}

						<li>
							<Bold>Category</Bold> should be only among the values given in the dropdown in the template. Depending on <Bold>Category</Bold>selected,{' '}
							<Bold>Subcategory {isSubSubCategoryRequired(groupId) ? ', Sub Sub Category' : ''}</Bold>
							can be selected and copied from the dropdown given below.
						</li>

						<li>
							<Bold>Alternate Text</Bold> should contain alphabets, numbers, hyphens and spaces only. It should be at max 100 characters in length.
						</li>

						{!restrictedFields.includes['keywords'] ? (
							<li>
								<Bold>Keywords</Bold> should contain alphabets, numbers and hyphens only. Each keyword can be at max 20 characters in length. Maximum 10 keywords are allowed. Multiple
								keywrds have to be separated using a <Bold>||</Bold> symbol. For example: "Gloves || Size-2 || Blue"
							</li>
						) : null}

						<li>
							<Bold>Attributes</Bold> have to follow a specific format. Attribute name and value should be written as <Bold>name : value</Bold>. Both name and value should be at max 50
							characters in length. Multiple attributes have to be separated using a <Bold>||</Bold> symbol. For example: "Color : Blue || Size : Large || Length : 1m"
						</li>

						<li>
							<Bold>Price Per Item</Bold> should be a {allowZeroPrice ? 'zero or positive' : 'positive'} numeric value, without currency symbol.
						</li>

						{!switchToInventoryV2 ? (
							<li>
								You can add <Bold>Scheduled At</Bold> timestamp to publish product on a future date. It should be a future date and should follow a specific format <Bold>YYYY-MM-DD</Bold>.
							</li>
						) : null}
					</ul>
				</FlexRow>

				<SectionRow>
					<InputGroup label='Find Manufacturer Alias'>
						<Select
							name='manufacturer'
							placeholder='Select value'
							value={manufacturer || ''}
							disabled={false}
							onChange={(e) => {
								this.handleInput('manufacturer', e.target.value);
							}}
							endAdornment={
								<InputAdornment>
									<CopyIcon
										onClick={(e) => {
											this.handleCopyInput(manufacturer);
										}}
									/>
								</InputAdornment>
							}
						>
							<MenuItem
								disabled={false}
								divider
								onClickCapture={(e) => {
									e.stopPropagation();
									e.preventDefault();
								}}
								onKeyDown={(e) => e.stopPropagation()}
							>
								<Search
									searchText={manufacturerSearchText}
									placeholder={'Search Manufacturer'}
									searchOnChange={true}
									onSearchHandler={(value) => this.setState({ manufacturerSearchText: value })}
								/>
							</MenuItem>

							{filteredManufacturerCount === 0 ? (
								<MenuItem
									key={'NO MANUFACTURER OPTIONS AVAILABLE'}
									value={''}
									disabled={true}
								>
									NO MANUFACTURER OPTIONS AVAILABLE
								</MenuItem>
							) : null}

							{filteredManufacturerOptions.map((item) => (
								<ManufacturerMenuItem
									key={item.value}
									value={item.value}
									disabled={item.disabled}
									onClick={(e) => { }}
								>
									{item.name}
								</ManufacturerMenuItem>
							))}

							<Pagination
								component='div'
								count={filteredManufacturerCount || 0}
								page={manufacturerPageNumber}
								onPageChange={(e, page) => {
									this.setState({ manufacturerPageNumber: parseInt(page, 10) });
								}}
								rowsPerPage={manufacturerPageSize}
								onRowsPerPageChange={(e) => {
									this.setState({ manufacturerPageSize: parseInt(e.target.value, 10), manufacturerPageNumber: 0 });
								}}
							/>
						</Select>

						{manufacturer ? (
							<span>
								Use <Bold>{manufacturer}</Bold> in XLSX
							</span>
						) : null}
					</InputGroup>

					{!(hideDSOInfo(groupId) || restrictedFields.includes('group_ids')) ? (
						<InputGroup label='Find DSO Alias'>
							<Select
								multiple
								name={'mapped_dso_ids'}
								placeholder='Select value'
								value={mappedDSOIds.split(' || ')}
								disabled={false}
								onChange={(e) => {
									this.handleInput('mappedDSOIds', e.target.value?.filter(item => item).join(' || '));
								}}
								endAdornment={
									<InputAdornment>
										<CopyIcon
											onClick={(e) => {
												this.handleCopyInput(mappedDSOIds);
											}}
										/>
									</InputAdornment>
								}
							>
								{!mappedDSOOptions.length ? (
									<MenuItem
										key={'NO DSO OPTIONS AVAILABLE'}
										value={''}
										disabled={true}
									>
										NO DSO OPTIONS AVAILABLE
									</MenuItem>
								) : null}

								{mappedDSOOptions.map((item) => (
									<MenuItem
										key={item.name}
										value={item.name}
										disabled={item.disabled}
									>
										{item.name}
									</MenuItem>
								))}
							</Select>

							{mappedDSOIds ? (
								<span>Use <Bold>{mappedDSOIds}</Bold> in XLSX</span>
							) : null}
						</InputGroup>
					) : null}

					<InputGroup label='Find Category Alias'>
						<Select
							name={'category'}
							placeholder='Select value'
							value={category || ''}
							disabled={false}
							onChange={(e) => {
								this.handleInput('category', e.target.value);
							}}
							endAdornment={
								<InputAdornment>
									<CopyIcon
										onClick={(e) => {
											this.handleCopyInput(category);
										}}
									/>
								</InputAdornment>
							}
						>
							{!categoryOptions.length ? (
								<MenuItem
									key={'NO CATEGORY OPTIONS AVAILABLE'}
									value={''}
									disabled={true}
								>
									NO CATEGORY OPTIONS AVAILABLE
								</MenuItem>
							) : null}

							{categoryOptions.map((item) => (
								<MenuItem
									key={item.value}
									value={item.value}
									disabled={item.disabled}
								>
									{item.name}
								</MenuItem>
							))}
						</Select>

						{category ? (
							<span>
								Use <Bold>{category}</Bold> in XLSX
							</span>
						) : null}
					</InputGroup>

					<InputGroup label='Find Sub Category Alias'>
						<Select
							name={'sub_category'}
							placeholder='Select value'
							value={subCategory || ''}
							disabled={!category}
							onChange={(e) => {
								this.handleInput('subCategory', e.target.value);
							}}
							endAdornment={
								<InputAdornment>
									<CopyIcon
										onClick={(e) => {
											this.handleCopyInput(subCategory);
										}}
									/>
								</InputAdornment>
							}
						>
							{!subCategoryOptions.length ? (
								<MenuItem
									key={'NO SUB CATEGORY OPTIONS AVAILABLE'}
									value={''}
									disabled={true}
								>
									NO SUB CATEGORY OPTIONS AVAILABLE
								</MenuItem>
							) : null}

							{subCategoryOptions.map((item) => (
								<MenuItem
									key={item.value}
									value={item.value}
									disabled={item.disabled}
								>
									{item.name}
								</MenuItem>
							))}
						</Select>

						{subCategory ? (
							<span>
								Use <Bold>{subCategory}</Bold> in XLSX
							</span>
						) : null}
					</InputGroup>

					{isSubSubCategoryRequired(groupId) ? (
						<InputGroup label='Find Sub Sub Category Alias'>
							<Select
								name={'sub_sub_category'}
								placeholder='Select value'
								value={subSubCategory || ''}
								disabled={!subCategory}
								onChange={(e) => {
									this.handleInput('subSubCategory', e.target.value);
								}}
								endAdornment={
									<InputAdornment>
										<CopyIcon
											onClick={(e) => {
												this.handleCopyInput(subSubCategory);
											}}
										/>
									</InputAdornment>
								}
							>
								{!subSubCategoryOptions.length ? (
									<MenuItem
										key={'NO SUB SUB CATEGORY OPTIONS AVAILABLE'}
										value={''}
										disabled={true}
									>
										NO SUB SUB CATEGORY OPTIONS AVAILABLE
									</MenuItem>
								) : null}

								{subSubCategoryOptions.map((item) => (
									<MenuItem
										key={item.value}
										value={item.value}
										disabled={item.disabled}
									>
										{item.name}
									</MenuItem>
								))}
							</Select>

							{subSubCategory ? (
								<span>
									Use <Bold>{subSubCategory}</Bold> in XLSX
								</span>
							) : null}
						</InputGroup>
					) : null}

					{allowMultipleUOM
						? <>
							<InputGroup label='Find UOM Alias'>
								<Select
									name={'opt_uom'}
									placeholder='Select value'
									value={uom || ''}
									disabled={false}
									onChange={(e) => {
										this.handleInput('uom', e.target.value);
									}}
									endAdornment={
										<InputAdornment>
											<CopyIcon
												onClick={(e) => {
													this.handleCopyInput(uom);
												}}
											/>
										</InputAdornment>
									}
								>
									{uomOptions.map((item) => (
										<MenuItem
											key={item.value}
											value={item.name}
											disabled={item.disabled}
										>
											{item.name}
										</MenuItem>
									))}
								</Select>
							</InputGroup>

							<InputGroup label='Find Packaging Alias'>
								<Select
									name={'opt_pkg'}
									placeholder='Select value'
									value={packaging || ''}
									disabled={false}
									onChange={(e) => {
										this.handleInput('packaging', e.target.value);
									}}
									endAdornment={
										<InputAdornment>
											<CopyIcon
												onClick={(e) => {
													this.handleCopyInput(packaging);
												}}
											/>
										</InputAdornment>
									}
								>
									{packagingOptions.map((item) => (
										<MenuItem
											key={item.value}
											value={item.name}
											disabled={item.disabled}
										>
											{item.name}
										</MenuItem>
									))}
								</Select>
							</InputGroup>
						</>
						: null}
				</SectionRow>
			</>
		);
	}

	render() {
		let { open, updateItems } = this.props;

		if (!open) return null;

		return (
			<>
				<Helmet>
					<title>{`Seller Portal | ${updateItems ? 'Update Items And Status In Bulk' : 'Add Items In Bulk'}`}</title>
				</Helmet>

				<BulkUploadDialog config={this.getBulkUploadWidgetConfig()}>{this.renderInstructionsAndPickers()}</BulkUploadDialog>
			</>
		);
	}
}

const mapStateToProps = (state) => {
	let restrictedFields = getRestrictedFields(state.session, state.supplier);
	let uomOptions = state.inventory.packagingOptions || [];

	return {
		groupId: state.session.groupId,
		supplier: state.supplier.supplier,
		session: state.session,
		isSupplierWithLivePrice: (state.supplier.suppliers_map?.[state.supplier.supplier] || {}).is_price_fetched || false,
		manufacturerOptions: state.inventory.manufacturerOptions || [],
		mappedSupplierOptions: state.inventory.mappedSupplierOptions || [],
		uomOptions,
		packagingOptions: uomOptions,
		mappedDSOOptions: state.inventory.mappedDSOOptions || [],
		categoryOptions: state.inventory.categoryOptions || [],
		subCategoryOptions: state.inventory.subCategoryOptions || [],
		subSubCategoryOptions: state.inventory.subSubCategoryOptions || [],
		restrictedFields: restrictedFields,
		allowZeroPrice: state?.supplier?.sellerPortalConfig?.pricing?.allow_zero_pricing?.base_price || false,
		allowMultipleUOM: state?.supplier?.sellerPortalConfig?.inventory?.enable_multi_uom || false,
		// scheduled_at is deprecated from v2 - see AddSingleItem.jsx's identical check for why the
		// "Scheduled At" instructions below are hidden once a supplier is on v2.
		switchToInventoryV2: getSellerPortalConfigFlag(state.supplier.sellerPortalConfig, state.session.sellerPortalConfig, 'inventory.enable_v2', true),
	};
};

const mapDispatch = {
	fetchAutocompleteSubCategory,
	fetchAutocompleteSubSubCategory,
	fetchAutocompleteSubCategoryV2,
	fetchAutocompleteSubSubCategoryV2,
	updateError,
	updateErrorMessage,
	updateMessage,
	updateSuccessMessage,
};

export default withRouter(connect(mapStateToProps, mapDispatch)(AddBulkItemsV2));
